import {
  DataGridPro,
  DataGridProProps,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box, Stack } from '@mui/material';
import i18n from '../../../libs/language';

type propTablet = {
  initialPageSize: number;
  noBorders?: boolean;
  noInternalBorders?: boolean;
  getDetailPanelContent?: (params: GridRowParams) => React.ReactNode;
  setPaginationModel?: (fn: (model: GridPaginationModel) => GridPaginationModel) => void;
  onRowSelected?: (id: GridRowId[]) => void;
  selectFullRow?: boolean;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  enableSelection?: boolean;
  // setSelectedRows?: (rows: any[]) => void;
};

export function BaseTable(props: DataGridProProps & propTablet) {
  const {
    rows = [],
    columns,
    initialPageSize,
    loading,
    rowHeight,
    noBorders,
    noInternalBorders,
    getDetailPanelContent,
    setPaginationModel,
    paginationMode,
    initialState,
    onRowSelected,
    selectFullRow,
    onPaginationModelChange,
    enableSelection = false,
    ...dataGridProProps
  } = props;

  const apiRef = useGridApiRef();

  const hidePagination = paginationMode === 'client' && rows.length <= initialPageSize;

  const handleRowSelectionChange = (model: GridRowSelectionModel) => {
    if (selectFullRow) {
      const selectedData = Array.from(model.ids).map((id) => rows.find((row) => row.id === id));
      if (onRowSelected) {
        onRowSelected(selectedData);
      }
    } else {
      if (onRowSelected) {
        onRowSelected(Array.from(model.ids));
      }
    }
  };

  return (
    <Stack>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '10px',
          overflowX: 'auto',
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          /* STYLES SECTION */
          sx={{
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            border: noBorders ? 'none' : '',
            'div.MuiToolbar-root.MuiToolbar-gutters.MuiToolbar-regular.MuiTablePagination-toolbar': {
              display: hidePagination ? 'none' : 'flex', // Mostrar u ocultar la paginación según la condición
            },
            '.MuiDataGrid-withBorderColor': {
              border: noBorders ? 'none' : '',
            },
            '.MuiDataGrid-cell': {
              border: noBorders || noInternalBorders ? 'none' : '',
            },
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'circular-progress' },
          }}
          /* SETTINGS SECTION */
          loading={loading}
          rowHeight={rowHeight ?? 60}
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: initialPageSize,
              },
            },
            ...initialState,
          }}
          localeText={{
            noRowsLabel: i18n.t('app.no_grid_data'),
            ...dataGridProProps.localeText,
            footerRowSelected: (count) =>
              count == 1
                ? `${count.toLocaleString()} ${i18n.t('app.row_selected')}`
                : `${count.toLocaleString()} ${i18n.t('app.rows_selected')}`,
          }}
          /* PAGINATION SECTION */
          pagination
          pageSizeOptions={[5, 10, 20, 50, 100]}
          paginationMode={paginationMode ?? 'client'}
          onPaginationModelChange={(newModel) => {
            if (!setPaginationModel) return;

            setPaginationModel((currentModel) => {
              if (newModel.pageSize === currentModel.pageSize) return newModel;

              const recalculatedPage = Math.floor(
                (currentModel.pageSize * currentModel.page) / newModel.pageSize
              );

              const newPaginationModel = {
                page: recalculatedPage,
                pageSize: newModel.pageSize,
              };
              onPaginationModelChange?.(newPaginationModel);

              return newPaginationModel;
            });
          }}
          checkboxSelection={enableSelection}
          checkboxSelectionVisibleOnly={enableSelection}
          onRowSelectionModelChange={handleRowSelectionChange}
          rowSelectionPropagation={{ parents: false, descendants: false }}
          /* GETTERS SECTION */
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={() => 'auto'}
          /* DISABLING SECTION */
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnReorder
          disableColumnResize
          /* USER CUSTOM PROPS SECTION */
          {...dataGridProProps}
        />
      </Box>
    </Stack>
  );
}
